// src/pages/lead/LeadShifts.jsx
// ============================================================
// Team Lead page for Shift Management.
// Leads can view defined shifts and assign them to their
// team members (but cannot create/delete shifts).
// ============================================================

import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  useShifts,
  useAllShiftAssignments,
  assignShift,
  removeShiftAssignment,
  seedStandardShifts,
} from "../../hooks/useFirestoreExtensions";
import { useLeadData } from "../../context/LeadContext";
import { LEAD_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";

// ── Format 24h → 12h ─────────────────────────────────────
function fmt12(time24) {
  if (!time24) return "";
  const [h, m] = time24.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

function cleanShiftName(name, start, end) {
  if (!name) return "";
  const lower = name.toLowerCase();
  if (lower.startsWith("gen") && !lower.startsWith("general")) {
    return "General Shift";
  }
  if (start && end) {
    const sClean = start.replace(/\s*[AP]M\s*/gi, "").trim();
    const eClean = end.replace(/\s*[AP]M\s*/gi, "").trim();
    if (
      (sClean === "09:00" || sClean === "9:00") &&
      (eClean === "18:00" || eClean === "6:00" || eClean === "06:00") &&
      name !== "Admin Working Hours"
    ) {
      return "General Shift";
    }
  }
  return name;
}

function calcDuration(start, end) {
  if (!start || !end) return "";
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins < 0) mins += 1440;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h${m ? ` ${m}m` : ""}`;
}

const SHIFT_TYPE_COLORS = {
  MORNING: "amber",
  GENERAL: "green",

  EVENING: "blue",
  NIGHT: "purple",
  custom: "gray",
};

const SHIFT_TYPE_ICONS = {
  MORNING: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      width="18"
      height="18"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"
      />
    </svg>
  ),

  GENERAL: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      width="18"
      height="18"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
      />
    </svg>
  ),


  EVENING: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      width="18"
      height="18"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 15a4.5 4.5 0 0 0 4.5 4.5H18a3.75 3.75 0 0 0 1.332-7.257 3 3 0 0 0-3.758-3.848 5.25 5.25 0 0 0-10.233 2.33A4.502 4.502 0 0 0 2.25 15Z"
      />
    </svg>
  ),

  NIGHT: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      width="18"
      height="18"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z"
      />
    </svg>
  ),

  custom: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      width="18"
      height="18"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
      />
    </svg>
  ),
};
export default function LeadShifts() {
  const { userProfile } = useAuth();
  const { members, membersLoading: mLoad } = useLeadData();
  const { data: shifts, loading: sLoad } = useShifts();
  const { data: assignments, loading: aLoad } = useAllShiftAssignments();

  const [assignState, setAssignState] = useState({});
  const [assigning, setAssigning] = useState({});
  const [assignMsg, setAssignMsg] = useState("");
  const [assignError, setAssignError] = useState("");

  // Seed standard shifts on load if authenticated (idempotent)
  useEffect(() => {
    if (userProfile?.uid) {
      seedStandardShifts(userProfile.uid).catch((err) =>
        console.warn("[LeadShifts] seedStandardShifts:", err)
      );
    }
  }, [userProfile?.uid]);
  const activeMembers = useMemo(
    () => members.filter((m) => m.isActive !== false),
    [members]
  );

  const assignedCount = useMemo(
    () =>
      activeMembers.filter((m) => {
        return assignments.some(
          (a) => a.userId === m.uid && a.status?.toUpperCase() === "APPROVED"
        );
      }).length,
    [assignments, activeMembers],
  );

  const unassignedCount = useMemo(
    () =>
      activeMembers.filter((m) => {
        const hasShift = assignments.some(
          (a) => a.userId === m.uid && (a.status?.toUpperCase() === "APPROVED" || a.status?.toUpperCase() === "PENDING_ADMIN" || a.status?.toUpperCase() === "PENDING ADMIN" || a.status?.toUpperCase() === "PENDING")
        );
        return !hasShift;
      }).length,
    [assignments, activeMembers],
  );

  async function handleAssign(userId) {
    const shiftId = assignState[userId];
    console.log("[LeadShifts] handleAssign called", { userId, shiftId });

    if (!shiftId) {
      console.warn("[LeadShifts] No shift selected for", userId);
      return;
    }

  const existingAssignment = assignments.find(
    (a) =>
      a.userId === userId &&
      a.shiftId === shiftId &&
      (a.status === "APPROVED" || a.status === "PENDING_ADMIN")
  );

  if (existingAssignment) {
    alert("This shift is already assigned/requested for this employee.");
    return;
  }




    const shift = shifts.find((s) => s.id === shiftId);
    if (!shift) {
      console.warn("[LeadShifts] Shift not found for id", shiftId);
      return;
    }
    if (!userProfile?.uid) {
      console.warn("[LeadShifts] userProfile not ready");
      return;
    }

    // Prevent duplicate request or multiple active/pending assignments
    const activeOrPending = assignments.find(
      (a) => a.userId === userId && (
        a.status?.toUpperCase() === "APPROVED" || 
        a.status?.toUpperCase() === "PENDING_ADMIN" || 
        a.status?.toUpperCase() === "PENDING ADMIN" || 
        a.status?.toUpperCase() === "PENDING"
      )
    );
    if (activeOrPending) {
      if (activeOrPending.status?.toUpperCase() === "APPROVED") {
        alert(`This employee already has an approved shift: ${cleanShiftName(activeOrPending.shiftName, activeOrPending.shiftStart, activeOrPending.shiftEnd)}. Remove it first to request a new one.`);
      } else {
        alert(`A shift request (${cleanShiftName(activeOrPending.shiftName, activeOrPending.shiftStart, activeOrPending.shiftEnd)}) is already pending admin approval for this employee.`);
      }
      return;
    }

    setAssigning((a) => ({ ...a, [userId]: true }));
    setAssignError("");
    try {
      const member = activeMembers.find(m => m.uid === userId);
      await assignShift({
        userId,
        employeeId: member?.employeeId || "",
        employeeName: member?.displayName || "",
        shiftId,
        shiftName: shift.name,
        shiftStart: shift.startTime,
        shiftEnd: shift.endTime,
        assignedBy: userProfile.uid,
        assignedByRole: "lead",
        teamLeadId: userProfile.uid,
        status: "PENDING_ADMIN",
      });
      console.log("[LeadShifts] request success", { userId, shiftId });
      setAssignState((s) => ({ ...s, [userId]: "" }));
      setAssignMsg(`Shift request for "${shift.name}" submitted to Admin.`);
      setTimeout(() => setAssignMsg(""), 4000);
    } catch (err) {
      console.error("[LeadShifts] assign:", err);
      setAssignError(err.message || "Failed to request shift assignment.");
      setTimeout(() => setAssignError(""), 5000);
    }
    setAssigning((a) => ({ ...a, [userId]: false }));
  }

  async function handleUnassign(assignmentId) {
    if (!window.confirm("Remove shift assignment/request?")) return;
    setAssigning((a) => ({ ...a, [assignmentId]: true }));
    setAssignError("");
    try {
      await removeShiftAssignment(assignmentId);
      setAssignMsg("Shift assignment/request removed successfully.");
      setTimeout(() => setAssignMsg(""), 4000);
    } catch (err) {
      console.error(err);
      setAssignError(err.message || "Failed to remove shift assignment.");
      setTimeout(() => setAssignError(""), 5000);
    }
    setAssigning((a) => ({ ...a, [assignmentId]: false }));
  }

  const loading = mLoad || sLoad || aLoad;

  return (
    <Layout navItems={LEAD_NAV} panelLabel="Team Lead Panel">
      <div className="page-header">
        <div>
          <h1 className="page-title">Shift Assignment</h1>
        </div>
      </div>

      {/* Summary chips */}
      <div
        className="attendance-summary-row"
        style={{ marginBottom: "1.5rem" }}
      >
        <div className="summary-chip summary-chip--blue">
          <span className="summary-chip-count">{activeMembers.length}</span>
          <span className="summary-chip-label">Team Members</span>
        </div>
        <div className="summary-chip summary-chip--green">
          <span className="summary-chip-count">{assignedCount}</span>
          <span className="summary-chip-label">Assigned</span>
        </div>
        <div className="summary-chip summary-chip--amber">
          <span className="summary-chip-count">{unassignedCount}</span>
          <span className="summary-chip-label">Unassigned</span>
        </div>
        <div className="summary-chip summary-chip--green">
          <span className="summary-chip-count">{shifts.filter(s => s.isActive !== false).length}</span>
          <span className="summary-chip-label">Shifts Available</span>
        </div>
      </div>

      {/* Shift reference cards */}
      {!sLoad && shifts.filter(s => s.isActive !== false).length > 0 && (
        <div style={{ marginBottom: "1.5rem" }}>
          <h3
            style={{
              fontSize: "0.8rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "var(--text-muted)",
              marginBottom: "0.75rem",
            }}
          >
            Available Shifts
          </h3>
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            {shifts.filter(s => s.isActive !== false).map((s) => {
              const color = SHIFT_TYPE_COLORS[s.type] || "gray";
              const icon =
  SHIFT_TYPE_ICONS[s.type] || (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      width="18"
      height="18"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 0 1 1.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.559.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.894.149c-.424.07-.764.383-.929.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 0 1-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.398.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 0 1-.12-1.45l.527-.737c.25-.35.272-.806.108-1.204-.165-.397-.506-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.108-1.204l-.526-.738a1.125 1.125 0 0 1 .12-1.45l.773-.773a1.125 1.125 0 0 1 1.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894Z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
      />
    </svg>
  );
              return (
                <div
                  key={s.id}
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius)",
                    padding: "0.6rem 1rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    fontSize: "0.82rem",
                  }}
                >
                  <span>{icon}</span>
                  <div>
                    <div style={{ fontWeight: 700 }}>{s.name}</div>
                    <div
                      style={{
                        color: "var(--text-muted)",
                        fontSize: "0.75rem",
                      }}
                    >
                      {fmt12(s.startTime)} → {fmt12(s.endTime)} (
                      {calcDuration(s.startTime, s.endTime)})
                    </div>
                  </div>
                  <span
                    className={`status-pill status-pill--${color}`}
                    style={{ marginLeft: "0.25rem" }}
                  >
                    {s.type}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Team member assignment table */}
      {loading ? (
        <div className="card-loading">Loading…</div>
      ) : activeMembers.length === 0 ? (
        <div className="card-empty">No team members found.</div>
      ) : (
        <>
          {assignMsg && (
            <div
              style={{
                background: "var(--green-bg)",
                color: "var(--green-text)",
                border: "1px solid var(--green)",
                borderRadius: "var(--radius)",
                padding: "0.65rem 1rem",
                fontSize: "0.85rem",
                marginBottom: "0.75rem",
              }}
            >
              ✅ {assignMsg}
            </div>
          )}
          {assignError && (
            <div
              style={{
                background: "#fde8e8",
                color: "#9b1c1c",
                border: "1px solid #f8b4b4",
                borderRadius: "var(--radius)",
                padding: "0.65rem 1rem",
                fontSize: "0.85rem",
                marginBottom: "0.75rem",
              }}
            >
              ❌ {assignError}
            </div>
          )}
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Department</th>
                  <th>Current Shift</th>
                  <th>Assign Shift</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {activeMembers.map((m) => {
                  const selShift = assignState[m.uid] ?? "";
                  const isAssigning = assigning[m.uid];

                  return (
                    <tr key={m.uid}>
                      <td>
                        <div className="table-user-cell">
                          <div className="table-avatar">
                            {m.displayName?.slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600 }}>
                              {m.displayName}
                            </div>
                            <div
                              style={{
                                fontSize: "0.72rem",
                                color: "var(--text-muted)",
                              }}
                            >
                              {m.employeeId}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>{m.department || "—"}</td>
                      <td>
                        {(() => {
                           const active = assignments.find(a => a.userId === m.uid && a.status?.toUpperCase() === 'APPROVED');
                           const pending = assignments.find(a => a.userId === m.uid && (a.status?.toUpperCase() === 'PENDING_ADMIN' || a.status?.toUpperCase() === 'PENDING ADMIN' || a.status?.toUpperCase() === 'PENDING'));
                          
                          if (active) {
                            const activeShift = shifts.find(s => s.id === active.shiftId);
                            return (
                              <div>
                                <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>
                                  {SHIFT_TYPE_ICONS[activeShift?.type] || "⚙️"} {cleanShiftName(active.shiftName, active.shiftStart, active.shiftEnd)}
                                </div>
                                {activeShift && (
                                  <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
                                    {fmt12(activeShift.startTime)} → {fmt12(activeShift.endTime)}
                                  </div>
                                )}
                                <span className="status-pill status-pill--green" style={{fontSize: '0.65rem', marginTop: '4px'}}>Approved</span>
                              </div>
                            );
                          }
                          
                          if (pending) {
                            return (
                              <div>
                                <div style={{ fontWeight: 600, fontSize: "0.85rem", opacity: 0.6 }}>
                                  {cleanShiftName(pending.shiftName, pending.shiftStart, pending.shiftEnd)}
                                </div>
                                <span className="status-pill status-pill--amber" style={{fontSize: '0.65rem', marginTop: '4px'}}>Pending Admin</span>
                              </div>
                            );
                          }

                          return <span className="status-pill status-pill--gray">Not Assigned</span>;
                        })()}
                      </td>
                      <td>
                        <select
                          className="filter-select"
                          value={selShift}
                          onChange={(e) =>
                            setAssignState((s) => ({
                              ...s,
                              [m.uid]: e.target.value,
                            }))
                          }
                          style={{ minWidth: "180px" }}
                          disabled={assignments.some(a => a.userId === m.uid && (
                            a.status?.toUpperCase() === 'APPROVED' || 
                            a.status?.toUpperCase() === 'PENDING_ADMIN' || 
                            a.status?.toUpperCase() === 'PENDING ADMIN' || 
                            a.status?.toUpperCase() === 'PENDING'
                          ))}
                          title={
                            (() => {
                              const hasApproved = assignments.some(a => a.userId === m.uid && a.status?.toUpperCase() === 'APPROVED');
                              if (hasApproved) return "This employee already has an approved shift. Remove it first to assign a new one.";
                              const hasPending = assignments.some(a => a.userId === m.uid && (a.status?.toUpperCase() === 'PENDING_ADMIN' || a.status?.toUpperCase() === 'PENDING ADMIN' || a.status?.toUpperCase() === 'PENDING'));
                              if (hasPending) return "A shift request is already pending admin approval for this employee.";
                              return "";
                            })()
                          }
                        >
                          <option value="">Select shift…</option>
                          {shifts.filter(s => s.isActive !== false).map((s) => (
                            <option key={s.id} value={s.id}>
                              {SHIFT_TYPE_ICONS[s.type]} {s.name} (
                              {fmt12(s.startTime)})
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <div className="action-btns">
                          <button
                            className="btn-success-sm"
                            onClick={() => handleAssign(m.uid)}
                            disabled={!selShift || !!isAssigning}
                          >
                            {isAssigning ? "…" : "Request"}
                          </button>
                          {(() => {
                            const latest = assignments.find(a => a.userId === m.uid && (a.status?.toUpperCase() === 'APPROVED' || a.status?.toUpperCase() === 'PENDING_ADMIN' || a.status?.toUpperCase() === 'PENDING ADMIN' || a.status?.toUpperCase() === 'PENDING'));
                            return latest ? (
                              <button
                                className="btn-danger-sm"
                                onClick={() => handleUnassign(latest.id)}
                                disabled={!!isAssigning}
                              >
                                ✕
                              </button>
                            ) : null;
                          })()}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Layout>
  );
}
